/**
 * Object → the two body shapes the endpoints that call `ReadFormAsync()` expect.
 *
 * Roughly half the action endpoints read a form rather than a JSON body — for example
 * every route under `/api/admin/invitations`. That is not a mistake to be fixed
 * server-side (R2/R3); it is the contract, and this is the adapter for it.
 *
 * Conversions are chosen to match how the server parses each field:
 *
 *   `true` / `false`  → "true" / "false"   `bool.TryParse` reads these and nothing else
 *   Date              → ISO 8601           `DateTime.TryParse` reads it in any culture
 *   null / undefined  → the key is DROPPED so the server's own default applies, rather
 *                       than sending the string "null" and having it parse as garbage
 *   Array             → one entry per item under the same key, which is what
 *                       `IFormCollection` binds to a `List<T>`
 */

function encode(value) {
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

const isFile = (value) => typeof File !== 'undefined' && value instanceof File
const isBlob = (value) => typeof Blob !== 'undefined' && value instanceof Blob

/** Applies one key/value pair to whichever container is being filled. */
function append(target, key, value) {
  if (value === null || value === undefined) return

  if (Array.isArray(value)) {
    value.forEach((item) => append(target, key, item))
    return
  }

  if (isFile(value) || isBlob(value)) {
    target.append(key, value)
    return
  }

  target.append(key, encode(value))
}

/**
 * `multipart/form-data`. The only shape that can carry a file, so this is what every
 * upload uses (see upload.js).
 */
export function toFormData(values, target = new FormData()) {
  for (const [key, value] of Object.entries(values ?? {})) append(target, key, value)
  return target
}

/**
 * `application/x-www-form-urlencoded`. Lighter than multipart and enough for the
 * file-less form posts, which is most of them.
 */
export function toUrlEncoded(values) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values ?? {})) append(params, key, value)
  return params
}
